import React, { useState } from "react";
import { toast } from "sonner";
import { X, Download, Trash, Warning } from "@phosphor-icons/react";
import { http } from "@/lib/api";

/**
 * Account Settings modal — GDPR / CCPA compliance UI (Sprint 60.45).
 *
 *   • Export My Data → hits /api/account/export, downloads a JSON dump.
 *   • Delete My Account → confirmation flow requiring password AND
 *     email retype AND explicit "DELETE" typed into a text field.
 *     Cancels active Stripe subscription and purges every user-scoped
 *     collection. Non-recoverable — we make that scary on purpose.
 *
 * This modal is opened from the header user-menu (see Header.jsx). It
 * lives OUTSIDE the routing tree so it can float over any page inside
 * the app shell without unmounting on navigation.
 */
export default function AccountSettings({ user, onClose }) {
  const [tab, setTab] = useState("billing"); // "billing" | "privacy" | "delete"
  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      data-testid="account-settings-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="max-w-lg w-full bg-[#0a0a0a] border-2 border-zinc-800 max-h-[90vh] overflow-y-auto">
        <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Account</div>
            <div className="heading font-black uppercase text-lg">{user?.email || "Settings"}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="account-settings-close"
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex gap-1 border-b border-zinc-900 px-2 py-2">
          <TabButton active={tab === "billing"} onClick={() => setTab("billing")} testId="tab-billing">
            Subscription
          </TabButton>
          <TabButton active={tab === "privacy"} onClick={() => setTab("privacy")} testId="tab-privacy">
            Your Data
          </TabButton>
          <TabButton active={tab === "delete"} onClick={() => setTab("delete")} testId="tab-delete-account">
            Delete Account
          </TabButton>
        </div>
        <div className="px-5 py-5">
          {tab === "billing" && <BillingTab user={user} />}
          {tab === "privacy" && <ExportTab />}
          {tab === "delete" && <DeleteTab onDone={onClose} email={user?.email} />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 mono text-[10px] uppercase tracking-widest transition-colors ${
        active ? "bg-[#39FF14] text-black font-black" : "text-zinc-500 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function BillingTab({ user }) {
  const [busy, setBusy] = useState(false);
  const status = user?.subscription_status || "inactive";
  const plan = (user?.plan || "trial").toUpperCase();
  const renews = user?.subscription_expires_at
    ? new Date(user.subscription_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;
  const isStripe = ["weekly", "monthly", "season"].includes(user?.plan);
  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await http.post("/billing/portal", { origin_url: window.location.origin });
      if (r.data?.url) window.location.assign(r.data.url);
      else toast.error("Billing portal unavailable");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4 text-sm text-zinc-300 leading-relaxed" data-testid="billing-tab">
      <div className="grid grid-cols-3 gap-2">
        <div className="border border-zinc-800 p-3">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Plan</div>
          <div className="heading font-black text-white mt-1" data-testid="billing-plan">{plan}</div>
        </div>
        <div className="border border-zinc-800 p-3">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Status</div>
          <div className={`heading font-black mt-1 ${status === "active" ? "text-[#39FF14]" : "text-[#FFCC00]"}`} data-testid="billing-status">
            {status.toUpperCase()}
          </div>
        </div>
        <div className="border border-zinc-800 p-3">
          <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">{isStripe ? "Renews" : "Access until"}</div>
          <div className="heading font-black text-white mt-1 text-sm" data-testid="billing-renews">{renews || "—"}</div>
        </div>
      </div>
      {isStripe ? (
        <>
          <div className="text-xs text-zinc-500">
            Update your card, download invoices, or cancel — changes take effect immediately and you keep access through the end of the current period.
          </div>
          <button
            onClick={openPortal}
            disabled={busy}
            data-testid="manage-subscription-button"
            className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 text-black font-black uppercase tracking-widest text-[11px] px-4 py-2.5 transition-colors"
          >
            {busy ? "Opening…" : "Manage Subscription · Cancel Anytime"}
          </button>
        </>
      ) : (
        <div className="text-xs text-zinc-500" data-testid="billing-non-stripe-note">
          {user?.plan === "beta"
            ? "Beta access — no billing on file."
            : "No paid subscription on file. Visit Pricing to start a plan."}
        </div>
      )}
    </div>
  );
}

function ExportTab() {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const r = await http.get("/account/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `warpaint-dfs-data-${r.data.user_id || "export"}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Your data export was downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
      <div>
        <div className="heading font-bold text-white mb-1">Export everything we have on you</div>
        <div className="text-xs text-zinc-500">
          Downloads a JSON file with your profile, subscription, uploads, lineups, and generation history.
          Full GDPR / CCPA compliance — request as often as you like.
        </div>
      </div>
      <button
        onClick={download}
        disabled={downloading}
        data-testid="export-data-button"
        className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 text-black font-black uppercase tracking-widest text-[11px] px-4 py-2.5 transition-colors"
      >
        <Download size={14} weight="bold" />
        {downloading ? "Preparing…" : "Download My Data (JSON)"}
      </button>
    </div>
  );
}

function DeleteTab({ email, onDone }) {
  const [password, setPassword] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = password.length >= 6
    && confirmEmail.trim().toLowerCase() === (email || "").toLowerCase()
    && confirmWord === "DELETE";

  const doDelete = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const r = await http.post("/account/delete", {
        password,
        confirm_email: confirmEmail.trim(),
      });
      toast.success(r.data.message || "Account deleted.", { duration: 8000 });
      // Force full reload — auth cookie is invalidated server-side but
      // localStorage might still hold stale UI state.
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
      <div className="border-2 border-[#FF3B30] bg-[#1a0808] px-3 py-2.5 flex items-start gap-2">
        <Warning size={18} weight="fill" className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
        <div className="text-[12px]">
          <div className="text-white font-bold uppercase tracking-widest text-[10px] mb-1">This cannot be undone</div>
          Deleting your account will cancel any active subscription (Stripe), purge all your uploads,
          lineups, and generation history, and permanently block this email from creating another
          account for 12 months. There is no "restore" option.
        </div>
      </div>
      <div className="space-y-3">
        <Field
          label="Confirm password"
          type="password"
          value={password}
          onChange={setPassword}
          testId="delete-password"
        />
        <Field
          label={`Retype your email (${email})`}
          value={confirmEmail}
          onChange={setConfirmEmail}
          testId="delete-confirm-email"
        />
        <Field
          label={`Type "DELETE" (case-sensitive) to confirm`}
          value={confirmWord}
          onChange={setConfirmWord}
          testId="delete-confirm-word"
        />
      </div>
      <div className="flex items-center justify-between gap-2 pt-2">
        <button
          onClick={onDone}
          className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
          data-testid="delete-cancel"
        >
          Cancel
        </button>
        <button
          onClick={doDelete}
          disabled={!canSubmit || busy}
          data-testid="delete-confirm"
          className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#e0332a] disabled:opacity-30 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest text-[11px] px-4 py-2.5 transition-colors"
        >
          <Trash size={14} weight="fill" />
          {busy ? "Deleting…" : "Delete My Account"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <label className="block">
      <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        autoComplete="off"
        className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2 text-sm text-white"
      />
    </label>
  );
}
